import { fetchWithAuth } from '../util';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

export interface ApiKey {
  id: string;
  key_id: string;
  name: string;
  api_key?: string; // Only present during creation
  key_preview: string;
  created_at: string;
  last_used_at?: string;
  is_active: boolean;
  usage_count?: number;
}

export interface CreateApiKeyRequest {
  name: string;
}

export interface UsageStats {
  total_requests: number;
  monthly_requests: number;
  input_tokens: number;
  output_tokens: number;
  top_endpoints: Array<{
    endpoint: string;
    count: number;
  }>;
}

// Timeline data interfaces
export interface TimelineData {
  date: string;
  requests: number;
  input_tokens: number;
  output_tokens: number;
  avg_response_time: number;
  error_rate: number;
}

export interface TimelineResponse {
  timeline: TimelineData[];
}

// Model usage data interfaces
export interface ModelUsageData {
  model: string;
  requests: number;
  avg_response_time: number;
  total_tokens: number;
  error_rate: number;
}

export interface ModelUsageResponse {
  models: ModelUsageData[];
}

// Performance metrics interfaces
export interface PerformanceData {
  endpoint: string;
  total_requests: number;
  avg_response_time: number;
  p95_response_time: number;
  avg_input_tokens: number;
  avg_output_tokens: number;
  error_rate: number;
}

export interface PerformanceResponse {
  performance: PerformanceData[];
}

// Error analysis interfaces
export interface ErrorData {
  status_code: number;
  count: number;
  percentage: number;
  [key: string]: string | number;
}

export interface RecentError {
  endpoint: string;
  method: string;
  status_code: number;
  error_message: string;
  created_at: string;
  model_used: string;
}

export interface ErrorAnalysisResponse {
  errors: ErrorData[];
  recent_errors: RecentError[];
}

export interface ApiKeyListResponse {
  api_keys: ApiKey[];
}

class ApiKeyService {
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const response = await fetchWithAuth(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      }
      if (response.status === 403) {
        throw new Error(
          'Access denied. API Manager requires admin or developer role.'
        );
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Request failed with status ${response.status}`
      );
    }

    return response.json();
  }

  async listApiKeys(): Promise<ApiKey[]> {
    const response = await this.makeRequest<ApiKeyListResponse>('/api/v1/keys');
    return response.api_keys;
  }

  async listAllApiKeys(): Promise<{
    api_keys: { key_id: string; name: string; is_active: boolean }[];
  }> {
    return this.makeRequest('/api/v1/keys/all-keys');
  }

  async createApiKey(data: CreateApiKeyRequest): Promise<ApiKey> {
    return this.makeRequest<ApiKey>('/api/v1/keys', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async revokeApiKey(keyId: string): Promise<void> {
    await this.makeRequest(`/api/v1/keys/${keyId}`, {
      method: 'DELETE',
    });
  }

  async deleteApiKey(keyId: string): Promise<void> {
    await this.makeRequest(`/api/v1/keys/${keyId}/delete`, {
      method: 'DELETE',
    });
  }

  async getUsageStats(): Promise<UsageStats> {
    return this.makeRequest<UsageStats>('/api/v1/keys/usage/stats');
  }

  async debugUsageData(): Promise<any> {
    return this.makeRequest<any>('/api/v1/keys/debug/usage');
  }

  async getUsageBreakdown(keyId?: string): Promise<any> {
    let url = '/api/v1/keys/usage/breakdown';
    if (keyId) {
      url += `?key_id=${keyId}`;
    }
    return this.makeRequest<any>(url);
  }

  // New analytics endpoints with optional date range support
  async getUsageTimeline(
    days: number = 30,
    startDate?: string,
    endDate?: string,
    allTime: boolean = false,
    keyId?: string
  ): Promise<TimelineResponse> {
    let url = `/api/v1/keys/usage/timeline?days=${days}`;
    if (allTime) {
      // For all time, use a very early start date to capture all data
      // Add one day to end date to ensure we capture today's data
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      url += `&start_date=2020-01-01&end_date=${tomorrow.toISOString().split('T')[0]}`;
    } else if (startDate && endDate) {
      url += `&start_date=${startDate}&end_date=${endDate}`;
    }
    if (keyId) {
      url += `&key_id=${keyId}`;
    }
    return this.makeRequest<TimelineResponse>(url);
  }

  async getModelUsage(
    days: number = 30,
    startDate?: string,
    endDate?: string,
    allTime: boolean = false,
    keyId?: string
  ): Promise<ModelUsageResponse> {
    let url = `/api/v1/keys/usage/models?days=${days}`;
    if (allTime) {
      // For all time, use a very early start date to capture all data
      // Add one day to end date to ensure we capture today's data
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      url += `&start_date=2020-01-01&end_date=${tomorrow.toISOString().split('T')[0]}`;
    } else if (startDate && endDate) {
      url += `&start_date=${startDate}&end_date=${endDate}`;
    }
    if (keyId) {
      url += `&key_id=${keyId}`;
    }
    return this.makeRequest<ModelUsageResponse>(url);
  }

  async getPerformanceMetrics(
    days: number = 30,
    startDate?: string,
    endDate?: string,
    allTime: boolean = false,
    keyId?: string
  ): Promise<PerformanceResponse> {
    let url = `/api/v1/keys/usage/performance?days=${days}`;
    if (allTime) {
      // For all time, use a very early start date to capture all data
      // Add one day to end date to ensure we capture today's data
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      url += `&start_date=2020-01-01&end_date=${tomorrow.toISOString().split('T')[0]}`;
    } else if (startDate && endDate) {
      url += `&start_date=${startDate}&end_date=${endDate}`;
    }
    if (keyId) {
      url += `&key_id=${keyId}`;
    }
    return this.makeRequest<PerformanceResponse>(url);
  }

  async getErrorAnalysis(
    days: number = 30,
    startDate?: string,
    endDate?: string,
    allTime: boolean = false,
    keyId?: string
  ): Promise<ErrorAnalysisResponse> {
    let url = `/api/v1/keys/usage/errors?days=${days}`;
    if (allTime) {
      // For all time, use a very early start date to capture all data
      // Add one day to end date to ensure we capture today's data
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      url += `&start_date=2020-01-01&end_date=${tomorrow.toISOString().split('T')[0]}`;
    } else if (startDate && endDate) {
      url += `&start_date=${startDate}&end_date=${endDate}`;
    }
    if (keyId) {
      url += `&key_id=${keyId}`;
    }
    return this.makeRequest<ErrorAnalysisResponse>(url);
  }
}

export const apiKeyService = new ApiKeyService();
